/**
 * Wave L / R4 — mesh editing commands on real topology.
 *
 * Boundary loops are ordered rings (not angle-sorted clouds), reduce is
 * edge-collapse (never deletes faces into holes), extrude builds side
 * walls (manifold, not a doubled shell), split actually cuts through the
 * CSG kernel, and MeshToNurb fits kernel surfaces through face groups.
 * (docs/plans/3d/3d-full-parity-plan.md R4)
 */

import {
    runInteraction, PK, PLoopK, PN, solidEntity, solidMesh, requireSelection,
    entityToPolyline, nurbsSurfaceEntity, deriveEntities,
} from './lib';
import { undoableReplace, undoableAdd } from '../entityOps';
import { useAppStore, type Entity } from '../../../state/store';
import {
    weldMesh, smoothMesh, refineMesh, reduceMesh, meshBoundaryLoops, centroid,
    triangulatePolygon, circlePoints,
} from '../geometryMath';
import type { MeshData, Pt3 } from '../geometryMath';
import { loftMesh } from '../meshFactory';
import { SolidKernel } from '../../../core/SolidKernel';
import * as THREE from 'three';
import { fitSurfaceFromPoints } from '../../geometry/nurbs/surface';
import type { RunnerMap } from '../runnerRegistry';

function pickOneMesh(label: string, fn: (m: MeshData) => MeshData | null, message: (m: MeshData) => string): string {
    runInteraction({
        steps: [PK(`${label}: pick a mesh/solid object`, ['solid'])],
        complete: (_p, _v, picks) => {
            const { entities } = useAppStore.getState();
            const m = solidMesh(entities[picks[0]]);
            if (!m) return `${label}: the picked object is not a mesh/solid.`;
            const result = fn(m);
            if (!result) return `${label}: the operation is not applicable to this mesh.`;
            undoableReplace(picks, [solidEntity(result, `${label}-${Date.now()}`, { closed: true })]);
            return message(result);
        },
    });
    return `${label}: pick a mesh`;
}

function pickedMeshes(picks: string[]): MeshData[] {
    const { entities } = useAppStore.getState();
    return picks.map((id) => solidMesh(entities[id])).filter(Boolean) as MeshData[];
}

export const meshRunners: RunnerMap = {
    'mesh.weld': () => pickOneMesh('WELD', (m) => weldMesh(m), (r) => `Welded: ${r.vertices.length} vertices.`),
    'mesh.unweld': () => pickOneMesh('UNWELD', (m) => ({
        // Unweld: duplicate shared corner vertices per face (hard edges).
        vertices: m.indices.flatMap(([a, b, c]) => [m.vertices[a], m.vertices[b], m.vertices[c]]),
        indices: m.indices.map((_, i) => [i * 3, i * 3 + 1, i * 3 + 2]),
    }), (r) => `Unwelded to ${r.indices.length} independent faces.`),
    'mesh.reduce': (args) => {
        const ratio = Number(args?.[0]) || 0.5;
        return pickOneMesh('REDUCEMESH', (m) => reduceMesh(m, ratio), (r) => `Reduced to ${r.indices.length} polygons (edge collapse, no holes).`);
    },
    'mesh.refine': () => pickOneMesh('REFINEMESH', (m) => refineMesh(m), (r) => `Refined to ${r.indices.length} triangles.`),
    'mesh.smooth': () => pickOneMesh('SMOOTHMESH', (m) => smoothMesh(m, 2), (r) => `Smoothed (${r.vertices.length} vertices).`),
    'mesh.fillHole': () => pickOneMesh('FILLMESHOLE', fillOneLoop, (r) => `Hole patched (${r.indices.length} faces).`),
    'mesh.fillHoles': () => pickOneMesh('FILLMESHOLES', (m) => {
        let out = m;
        let guard = 0;
        while (meshBoundaryLoops(out).length > 0 && guard++ < 32) {
            const next = fillOneLoop(out);
            if (!next || next.indices.length === out.indices.length) break;
            out = next;
        }
        return out;
    }, (r) => `All boundary loops patched (${r.indices.length} faces).`),
    'mesh.split': () => {
        runInteraction({
            steps: [PK('MESHSPLIT: pick a mesh', ['solid']), PK('MESHSPLIT: pick the cutter mesh', ['solid'])],
            complete: (_p, _v, picks) => {
                const list = pickedMeshes(picks);
                if (list.length < 2) return 'MESHSPLIT: both picks must be meshes.';
                try {
                    const a = toWorldMesh(list[0]);
                    const b = toWorldMesh(list[1]);
                    // split = (a − b) and (a ∩ b) through the CSG kernel
                    const keepPart = fromWorldMesh(SolidKernel.subtract(a, b));
                    const cutPart = fromWorldMesh(SolidKernel.intersect(toWorldMesh(list[0]), b));
                    if (keepPart.indices.length === 0 && cutPart.indices.length === 0) {
                        return 'MESHSPLIT: the cutter does not intersect the mesh.';
                    }
                    const created = [keepPart, cutPart].filter((m) => m.indices.length > 0)
                        .map((m, i) => solidEntity(m, `MeshSplit-${Date.now()}-${i}`, { closed: true }));
                    undoableReplace([picks[0]], created);
                    return `MESHSPLIT: mesh cut into ${created.length} piece(s) via CSG.`;
                } catch {
                    return 'MESHSPLIT: CSG split failed (non-manifold input).';
                }
            },
        });
        return 'MESHSPLIT: pick mesh + cutter';
    },
    'mesh.extrude': (args) => {
        const h = Number(args?.[0]) || 0.5;
        runInteraction({
            steps: [PK('EXTRUDEMESH: pick a planar mesh', ['solid']), PN('EXTRUDEMESH: height <0.5>')],
            complete: (_p, values, picks) => {
                const list = pickedMeshes(picks);
                if (list.length === 0) return 'EXTRUDEMESH: nothing picked.';
                const height = Number(values[0]) || h;
                const m = list[0];
                // Real solid extrude: bottom + lifted top + quad walls along
                // every boundary loop (manifold, not a doubled shell).
                const loops = meshBoundaryLoops(m);
                if (loops.length === 0) return 'EXTRUDEMESH: mesh is already closed (nothing to extrude).';
                const n = m.vertices.length;
                const top = m.vertices.map((v) => [v[0], v[1], (v[2] ?? 0) + height]);
                const indices = [...m.indices.map((t) => [t[2], t[1], t[0]]), ...m.indices.map((t) => [t[0] + n, t[1] + n, t[2] + n])];
                loops.forEach((loop) => {
                    for (let i = 0; i < loop.length; i++) {
                        const a = loop[i], b = loop[(i + 1) % loop.length];
                        indices.push([a, b, b + n], [a, b + n, a + n]);
                    }
                });
                undoableReplace(picks, [solidEntity({ vertices: [...m.vertices, ...top], indices }, `MeshExtrude-${Date.now()}`, { closed: true })]);
                return `Mesh extruded by ${height} (manifold with ${loops.length} boundary ring(s) walled).`;
            },
        });
        return 'EXTRUDEMESH: pick a mesh';
    },
    'mesh.bridge': () => {
        runInteraction({
            steps: [PK('BRIDGE: pick the first mesh', ['solid']), PK('BRIDGE: pick the second mesh', ['solid'])],
            complete: (_p, _v, picks) => {
                const created = deriveEntities('mesh.bridge', picks, (sources) => {
                    const list = sources.map((e) => solidMesh(e)).filter(Boolean) as MeshData[];
                    if (list.length < 2) return null;
                    const [a, b] = list;
                    const la = meshBoundaryLoops(a), lb = meshBoundaryLoops(b);
                    if (la.length === 0 || lb.length === 0) return null;
                    // bridge the two most compatible loops (nearest centroids)
                    let bi = 0, bj = 0, bd = Infinity;
                    la.forEach((x, i) => lb.forEach((y, j) => {
                        const cx = centroid(x.map((k) => a.vertices[k] as Pt3));
                        const cy = centroid(y.map((k) => b.vertices[k] as Pt3));
                        const d = Math.hypot(cx[0] - cy[0], cx[1] - cy[1], cx[2] - cy[2]);
                        if (d < bd) { bd = d; bi = i; bj = j; }
                    }));
                    const ringA = la[bi].map((k) => a.vertices[k] as Pt3);
                    const ringB = lb[bj].map((k) => b.vertices[k] as Pt3);
                    if (ringB.length < 2) return null;
                    const strip = loftMesh([ringA, ringB]);
                    if (strip.indices.length === 0) return null;
                    return [solidEntity(strip, `Bridge-${Date.now()}`, { closed: false, wire: false })];
                });
                if (!created) return 'BRIDGE: both meshes need open boundary loops.';
                const m = (created[0].data.indices as number[][]).length;
                return `Bridge band created between boundary loops (${m} quads).`;
            },
        });
        return 'BRIDGE: pick two meshes';
    },
    'mesh.stitch': () => {
        runInteraction({
            steps: [PLoopK('STITCH: pick the two meshes to stitch', 2)],
            complete: (_p, _v, picks) => {
                const list = pickedMeshes(picks);
                if (list.length < 2) return 'STITCH: pick two meshes.';
                // Stitch = move coincident-near boundary loops together, then
                // weld. Collect both boundary vertex clouds and pull vertices
                // within 10% of scene extent onto their nearest partner.
                const merged: MeshData = { vertices: [], indices: [] };
                list.forEach((m) => {
                    const off = merged.vertices.length;
                    merged.vertices.push(...m.vertices.map((v) => [...v]));
                    m.indices.forEach((t) => merged.indices.push(t.map((i) => i + off)));
                });
                const loops = meshBoundaryLoops(merged);
                if (loops.length < 2) {
                    const welded = weldMesh(merged, 1e-3);
                    undoableReplace(picks, [solidEntity(welded, `Stitched-${Date.now()}`, { closed: true })]);
                    return 'STITCH: welded coincident vertices (single boundary found).';
                }
                // snap boundary vertices of different loops that lie near each other
                const bounds = merged.vertices.reduce((acc, v) => [Math.max(acc[0], v[0]), Math.max(acc[1], v[1] ?? 0), Math.max(acc[2], v[2] ?? 0)], [0, 0, 0]);
                const tol = Math.max(...bounds) * 0.05;
                const byLoop = loops.map((l) => new Set(l));
                for (let i = 0; i < loops.length; i++) {
                    for (let j = i + 1; j < loops.length; j++) {
                        loops[i].forEach((va) => {
                            const pa = merged.vertices[va];
                            let best = -1, bd = tol;
                            loops[j].forEach((vb) => {
                                const pb = merged.vertices[vb];
                                const d = Math.hypot(pa[0] - pb[0], (pa[1] ?? 0) - (pb[1] ?? 0), (pa[2] ?? 0) - (pb[2] ?? 0));
                                if (d < bd) { bd = d; best = vb; }
                            });
                            if (best >= 0 && byLoop[i].has(va) && byLoop[j].has(best)) {
                                merged.vertices[va] = [...merged.vertices[best]];
                            }
                        });
                    }
                }
                const welded = weldMesh(merged, 1e-3);
                undoableReplace(picks, [solidEntity(welded, `Stitched-${Date.now()}`, { closed: true })]);
                return `Stitched: boundary loops snapped and welded to ${welded.vertices.length} vertices.`;
            },
        });
        return 'STITCH: pick two meshes';
    },
    'mesh.createFromObj': (args) => {
        runInteraction({
            steps: [PLoopK('MESH: pick objects to convert', 1)],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                let done = 0;
                picks.forEach((id) => {
                    const e = entities[id];
                    if (!e || e.type === 'solid') return;
                    const mesh = meshOfEntity(e);
                    if (mesh) {
                        undoableReplace([id], [solidEntity(mesh, `Mesh-${Date.now()}`, { closed: false })]);
                        done++;
                    }
                });
                void args;
                return done > 0 ? `MESH: converted ${done} object(s) to mesh.` : 'MESH: nothing convertible picked.';
            },
        });
        return 'MESH: pick objects';
    },
    'mesh.toNurb': () => {
        runInteraction({
            steps: [PK('MESHTONURB: pick a mesh', ['solid']), PN('MESHTONURB: faces per patch <8>')],
            complete: (_p, values, picks) => {
                const perPatch = Math.max(4, Number(values[0]) || 8);
                const created = deriveEntities('mesh.toNurb', picks, (sources) => {
                    const m = solidMesh(sources[0]);
                    if (!m || m.vertices.length === 0) return null;
                    // Group faces into local clusters (connected components over
                    // a face-budget), then least-squares FIT A REAL NURBS
                    // SURFACE per cluster instead of one-triangle fakes.
                    const clusters: number[][] = [];
                    const faceAdj = new Map<string, number[]>();
                    const faceKey = (a: number, b: number) => (a < b ? `${a}:${b}` : `${b}:${a}`);
                    m.indices.forEach((t, fi) => {
                        for (let e = 0; e < 3; e++) {
                            const k = faceKey(t[e], t[(e + 1) % 3]);
                            faceAdj.set(k, [...(faceAdj.get(k) ?? []), fi]);
                        }
                    });
                    const seen = new Set<number>();
                    m.indices.forEach((_, fi) => {
                        if (seen.has(fi)) return;
                        const cluster: number[] = [];
                        const queue = [fi];
                        while (queue.length && cluster.length < perPatch) {
                            const f = queue.shift()!;
                            if (seen.has(f)) continue;
                            seen.add(f);
                            cluster.push(f);
                            const t = m.indices[f];
                            for (let e = 0; e < 3; e++) {
                                (faceAdj.get(faceKey(t[e], t[(e + 1) % 3])) ?? []).forEach((nb) => { if (!seen.has(nb)) queue.push(nb); });
                            }
                        }
                        clusters.push(cluster);
                    });
                    const out: Entity[] = [];
                    clusters.forEach((cluster) => {
                        const pts: Pt3[] = [];
                        cluster.forEach((fi) => m.indices[fi].forEach((vi) => {
                            const v = m.vertices[vi];
                            pts.push([v[0], v[1], v[2] ?? 0]);
                        }));
                        const grid = Math.max(4, Math.min(10, Math.round(Math.sqrt(pts.length))));
                        const srf = fitSurfaceFromPoints(pts, grid, grid);
                        if (srf) out.push(nurbsSurfaceEntity(srf, `Nurb-${Date.now()}-${out.length}`));
                    });
                    return out.length ? out : null;
                });
                return created
                    ? `MESHTONURB: ${created.length} NURBS patch(es) fitted over face clusters.`
                    : 'MESHTONURB: surface fit failed (pick a mesh).';
            },
        });
        return 'MESHTONURB: pick a mesh';
    },
};

// ---------------- helpers ----------------

/** Fill the single largest boundary loop with a centroid fan (concave-safe). */
function fillOneLoop(m: MeshData): MeshData | null {
    const loops = meshBoundaryLoops(m);
    if (loops.length === 0) return null;
    const loop = loops.sort((a, b) => b.length - a.length)[0];
    const pts = loop.map((i) => m.vertices[i] as Pt3);
    const c = centroid(pts);
    const ci = m.vertices.length;
    const indices = [...m.indices];
    // fan with an added centroid vertex: robust for concave loops
    loop.forEach((vi, i) => {
        const next = loop[(i + 1) % loop.length];
        indices.push([vi, next, ci]);
    });
    return { vertices: [...m.vertices, [c[0], c[1], c[2]]], indices };
}

function meshOfEntity(e: { type: string; data: Record<string, any> }): MeshData | null {
    if (e.type === 'line' || e.type === 'polyline' || e.type === 'rectangle' || e.type === 'polygon') {
        const pts: number[][] = e.type === 'line' ? [e.data.start, e.data.end] : (e.data.vertices as number[][]);
        if (!pts || pts.length < 2) return null;
        if (e.type === 'rectangle' || e.type === 'polygon') {
            return { vertices: pts.map((p) => [...p]), indices: triangulatePolygon(pts.map((p) => [p[0], p[1], p[2] ?? 0] as Pt3)) };
        }
        // open path: ribbon of the real path length (0.02 half-width)
        const verts: number[][] = [];
        const idx: number[][] = [];
        for (let i = 0; i < pts.length - 1; i++) {
            const a = pts[i], b = pts[i + 1];
            const dx = b[0] - a[0], dy = b[1] - a[1];
            const l = Math.hypot(dx, dy) || 1;
            const ox = (-dy / l) * 0.02, oy = (dx / l) * 0.02;
            const base = verts.length;
            verts.push([a[0] + ox, a[1] + oy, a[2] ?? 0], [a[0] - ox, a[1] - oy, a[2] ?? 0],
                [b[0] + ox, b[1] + oy, b[2] ?? 0], [b[0] - ox, b[1] - oy, b[2] ?? 0]);
            idx.push([base, base + 1, base + 2], [base + 1, base + 3, base + 2]);
        }
        return { vertices: verts, indices: idx };
    }
    if (e.type === 'circle' || e.type === 'arc' || e.type === 'ellipse') {
        const c = e.data.center as number[];
        if (!c) return null;
        const poly = e.type === 'circle'
            ? circlePoints(c[0], c[1], e.data.radius ?? 1, c[2] ?? 0)
            : (() => {
                const seg = 48;
                const pts: Pt3[] = [];
                if (e.type === 'ellipse') {
                    for (let i = 0; i <= seg; i++) {
                        const t = (i / seg) * Math.PI * 2;
                        const x = (e.data.radiusA ?? 1) * Math.cos(t), y = (e.data.radiusB ?? 1) * Math.sin(t);
                        const rot = e.data.rotation ?? 0;
                        pts.push([c[0] + x * Math.cos(rot) - y * Math.sin(rot), c[1] + x * Math.sin(rot) + y * Math.cos(rot), c[2] ?? 0]);
                    }
                } else {
                    let sweep = (e.data.endAngle ?? Math.PI) - (e.data.startAngle ?? 0);
                    while (sweep <= 0) sweep += Math.PI * 2;
                    const n = Math.max(8, Math.round((sweep / (Math.PI * 2)) * seg));
                    for (let i = 0; i <= n; i++) {
                        const a = (e.data.startAngle ?? 0) + (sweep * i) / n;
                        pts.push([c[0] + Math.cos(a) * (e.data.radius ?? 1), c[1] + Math.sin(a) * (e.data.radius ?? 1), c[2] ?? 0]);
                    }
                }
                return pts;
            })();
        const closed = poly.length >= 3 && Math.hypot(poly[0][0] - poly[poly.length - 1][0], poly[0][1] - poly[poly.length - 1][1]) < 1e-6;
        const ring = closed ? poly.slice(0, -1) : poly;
        const indices = ring.length >= 3
            ? triangulatePolygon(ring)
            : [];
        return { vertices: ring.map((p) => [...p]), indices };
    }
    const poly = entityToPolyline(e as any);
    if (poly && poly.length >= 3) {
        return { vertices: poly.map((p) => [...p]), indices: triangulatePolygon(poly) };
    }
    return null;
}

/** Wrap a mesh payload into a world-space THREE.Mesh for CSG (identity pos). */
function toWorldMesh(m: MeshData): THREE.Mesh {
    const g = new THREE.BufferGeometry();
    const pos = new Float32Array(m.vertices.length * 3);
    m.vertices.forEach((v, i) => { pos[i * 3] = v[0]; pos[i * 3 + 1] = v[1]; pos[i * 3 + 2] = v[2] ?? 0; });
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    const idx: number[] = [];
    m.indices.forEach((t) => idx.push(t[0], t[1], t[2]));
    g.setIndex(idx);
    g.computeVertexNormals();
    return new THREE.Mesh(g);
}

function fromWorldMesh(mesh: THREE.Mesh): MeshData {
    const g = mesh.geometry as THREE.BufferGeometry;
    const p = g.getAttribute('position') as THREE.BufferAttribute;
    const vertices: number[][] = [];
    for (let i = 0; i < p.count; i++) vertices.push([p.getX(i), p.getY(i), p.getZ(i)]);
    const indices: number[][] = [];
    const index = g.getIndex();
    if (index) {
        for (let i = 0; i < index.count; i += 3) indices.push([index.getX(i), index.getX(i + 1), index.getX(i + 2)]);
    }
    g.dispose();
    return { vertices, indices };
}
void requireSelection;
